/**
 * Tipos do núcleo de negócio.
 *
 * Restrição arquitetural (decisões D3 e D6): este pacote roda no navegador e na Lambda.
 * Não pode importar nada de `apps/` nem de `services/`, nem usar API de plataforma.
 *
 * Restrição de sintaxe: o pacote é executado diretamente pelo Node por remoção de tipos
 * (`erasableSyntaxOnly`). Não usar `enum`, `namespace` nem propriedades de parâmetro de construtor.
 */

// ---------------------------------------------------------------------------
// Trilhas e planos
// ---------------------------------------------------------------------------

/** Trilha comercial. CS = Commercial Sector, PS = Public Sector. */
export type Trilha = 'CS' | 'PS';

/**
 * Plano de suporte SafePlace.
 *
 * `nenhum` é o modo introduzido na V2: o cliente opera com equipe própria e a eMaster atua como
 * parceira de faturamento e de acesso ao ecossistema AWS. Usa a margem da faixa Developer e
 * não cobra o plano. Ver `regras-negocio.md`, seção 3.3.
 */
export type PlanoSuporte = 'nenhum' | 'developer' | 'business' | 'enterprise';

/** Duração do contrato em meses. Afeta apenas a margem, nunca o custo do suporte. */
export type DuracaoContratoMeses = 12 | 36;

/** Janela de distribuição do Crédito Especial AWS via eMaster. */
export type JanelaCreditoMeses = 3 | 4 | 5 | 6 | 12;

/** Moeda. `USD` é a moeda funcional interna de todo o cálculo. */
export type Moeda = 'USD' | 'BRL';

/**
 * Modalidade do custo de deploy.
 *
 * - `nao_incluido`: não há custo de deploy na proposta.
 * - `faturado`: pagamento único, cobrado do cliente.
 * - `financiado`: coberto pelo Crédito Especial AWS, que tem o deploy como prioridade.
 * - `cortesia`: não cobrado, mas exibido na proposta para evidenciar o benefício concedido.
 */
export type ModalidadeDeploy = 'nao_incluido' | 'faturado' | 'financiado' | 'cortesia';

/** Licenças do produto eMaster Backup On-Premises. Preços fixos em BRL, sem margem. */
export type LicencaBackup = 'fileServer' | 'virtualServer' | 'sharepoint' | 'vmwareHyperv';

/**
 * Foco do assessment SafePlace oferecido como cortesia.
 *
 * São os pilares do AWS Well-Architected Framework que a eMaster oferece avaliar sem custo. A
 * proposta guarda uma **lista**, e não um único valor: o vendedor pode oferecer um foco ou os dois.
 *
 * A lista é o que mantém o renderizador simples. Com um valor único mais um caso "ambos", cada
 * consumidor — seção web, termo formal, navegação — teria de expandir esse caso por conta própria, e
 * a combinação viraria regra repetida em vez de dado. Percorrer a lista funciona igual para um ou
 * dois focos, e continua funcionando se um terceiro pilar entrar no catálogo.
 */
export type FocoAssessment = 'custos' | 'seguranca';

/**
 * Plano do eMaster SafeStorage, a assinatura SaaS por faixa de usuários.
 *
 * Ao contrário do backup, o preço de tabela é em **USD** — é assinatura de plataforma, não licença
 * revendida em reais. Isso mantém o SafeStorage fora da regra que obriga o PTAX em CS.
 *
 * `enterprise` é a faixa acima de 50 usuários, cujo valor é "sob consulta": o vendedor informa a
 * mensalidade negociada em `mensalidadeCustomizadaUsd`.
 */
export type PlanoSafeStorage = 'orbita' | 'constelacao' | 'singularidade' | 'enterprise';

// ---------------------------------------------------------------------------
// Entrada de precificação
// ---------------------------------------------------------------------------

/** Item de custo importado do CSV da AWS Pricing Calculator, com o custo líquido preservado. */
export interface ItemCusto {
  readonly servico: string;
  readonly descricao: string;
  readonly resumoConfiguracao: string;
  readonly grupo: string;
  /** Custo mensal líquido da AWS, em USD, antes da margem. */
  readonly custoMensalLiquidoUsd: number;
}

export interface EntradaCredito {
  /** Valor do crédito, sempre em USD, mesmo na trilha PS. */
  readonly valorUsd: number;
  readonly janelaMeses: JanelaCreditoMeses;
}

export interface EntradaDeploy {
  readonly modalidade: ModalidadeDeploy;
  /**
   * Custo do deploy na moeda de entrada da trilha: USD em CS, BRL em PS.
   * Ignorado quando a modalidade é `nao_incluido`.
   */
  readonly custo: number;
}

/** Quantidade de licenças por tipo. Ausente ou zero significa que o produto não foi incluído. */
export type EntradaBackup = Readonly<Partial<Record<LicencaBackup, number>>>;

/**
 * Assinatura do eMaster SafeStorage.
 *
 * O armazenamento S3 **não** entra aqui: ele é consumo de infraestrutura e percorre o caminho normal
 * (`itensCusto` do CSV ou `consumoManual`), recebendo margem como qualquer recurso AWS. O que este
 * bloco descreve é só a assinatura da plataforma, que tem preço de tabela e não recebe margem.
 *
 * Separar as duas coisas é o que permite ao cliente ver na proposta a promessa do produto: plataforma
 * previsível por faixa de usuários, armazenamento proporcional ao que ele consumir.
 */
export interface EntradaSafeStorage {
  readonly plano: PlanoSafeStorage;
  /** Quantidade de usuários contratada. Precisa caber na faixa do plano escolhido. */
  readonly usuarios: number;
  /**
   * Mensalidade negociada, em USD. Obrigatória quando o plano é `enterprise` (sob consulta) e
   * ignorada nos demais, que têm preço de tabela.
   */
  readonly mensalidadeCustomizadaUsd: number | null;
}

/**
 * Custo adicional avulso, com nome dado pelo vendedor.
 *
 * Existe para o que não tem campo próprio no catálogo: um módulo cobrado à parte, uma licença de
 * terceiro, um posto de operação. O exemplo que motivou o campo é "Módulo SafePlace 24x7".
 *
 * O valor é **preço de venda**, não custo: nenhuma margem é aplicada, pela mesma razão do backup e
 * do SafeStorage. Quem digita já digita o que o cliente paga.
 *
 * A moeda é a **moeda de entrada da trilha** — USD em CS, BRL em PS — e não uma moeda fixa. É o que
 * evita reabrir o defeito D2: o backup, por ser sempre em BRL, obrigou o PTAX até na trilha CS. Aqui
 * a trilha CS informa em dólar, não há conversão, e o PTAX continua exigido só onde já era.
 */
export interface ItemCustoAdicional {
  /** Nome do item como aparece na proposta. Ex.: "Módulo SafePlace 24x7". */
  readonly descricao: string;
  /** Valor mensal na moeda de entrada da trilha: USD em CS, BRL em PS. */
  readonly valorMensal: number;
}

export interface EntradaPrecificacao {
  readonly trilha: Trilha;
  readonly duracaoContratoMeses: DuracaoContratoMeses;
  readonly planoSuporte: PlanoSuporte;

  /**
   * Cotação do dólar. Obrigatória quando a trilha é `PS` ou quando há licenças de backup,
   * porque o backup é precificado em BRL e compõe a mensalidade (correção do defeito D2).
   */
  readonly ptax: number | null;

  /**
   * Itens do CSV da AWS Pricing Calculator.
   *
   * Quando presente e não vazio, a margem é aplicada item a item. Quando `null`, nenhuma margem
   * é aplicada e `consumoManual` é tratado como preço de venda já formado.
   * Ver `regras-negocio.md`, seção 2.4.
   */
  readonly itensCusto: readonly ItemCusto[] | null;

  /**
   * Consumo digitado pelo vendedor, na moeda de entrada da trilha: USD em CS, BRL em PS.
   * Usado somente quando `itensCusto` é `null`.
   */
  readonly consumoManual: number | null;

  readonly credito: EntradaCredito | null;
  readonly deploy: EntradaDeploy;
  readonly backup: EntradaBackup | null;
  /** Assinatura do eMaster SafeStorage. `null` quando o produto não está na proposta. */
  readonly safestorage: EntradaSafeStorage | null;
  /**
   * Custos adicionais nomeados pelo vendedor. `null` ou ausente quando não há nenhum.
   *
   * Opcional de propósito, e não obrigatório como `safestorage`: o campo nasceu depois que o motor já
   * tinha chamadores — o painel e a bateria de testes. Torná-lo obrigatório quebraria todos eles de
   * uma vez sem ganho de correção, porque a ausência tem significado claro e único: nenhum custo
   * adicional. O motor trata ausência como lista vazia.
   */
  readonly custosAdicionais?: readonly ItemCustoAdicional[] | null;
}

// ---------------------------------------------------------------------------
// Resultado da precificação
// ---------------------------------------------------------------------------

/** Item de custo depois da aplicação da margem. */
export interface ItemPrecificado extends ItemCusto {
  /** Preço de venda do item, em USD, após o gross-up da margem. */
  readonly precoVendaUsd: number;
}

/**
 * Valor monetário com as duas moedas resolvidas, para a camada de apresentação não precisar
 * conhecer a cotação. `brl` é `null` quando não há PTAX disponível.
 */
export interface Valor {
  readonly usd: number;
  readonly brl: number | null;
}

export interface ResultadoPrecificacao {
  /** Versão da configuração de constantes usada, para permitir reconstruir a proposta. */
  readonly versaoConfiguracao: string;
  readonly ptaxAplicado: number | null;

  /** Margem efetivamente aplicada, em pontos percentuais. Zero quando não houve CSV. */
  readonly margemPercentual: number;
  /** `true` quando a margem foi aplicada, isto é, quando havia CSV. */
  readonly margemAplicada: boolean;

  readonly itensPrecificados: readonly ItemPrecificado[];

  /** Soma dos custos líquidos da AWS, antes da margem. `null` quando não houve CSV. */
  readonly custoLiquidoTotal: Valor | null;
  /** Consumo com margem, ou o consumo manual quando não houve CSV. */
  readonly consumo: Valor;
  readonly custoSuporte: Valor;
  /** Consumo + suporte. Rótulo comercial: "Total Previsto (Infraestrutura + Suporte)". */
  readonly baseMensal: Valor;

  readonly licencasBackup: Valor;

  /**
   * Assinatura mensal da plataforma SafeStorage. Zero quando o produto não está na proposta.
   *
   * É o preço de tabela da faixa, sem margem: a margem do SafeStorage está no armazenamento, que
   * entra por `consumo`.
   */
  readonly plataformaSafeStorage: Valor;

  /**
   * Soma dos custos adicionais nomeados pelo vendedor. Zero quando não há nenhum.
   *
   * Sem margem: os valores informados já são preço de venda.
   */
  readonly custosAdicionais: Valor;

  /** Custo total do deploy, exibido inclusive quando cortesia. */
  readonly deployCustoTotal: Valor;
  /** Parcela do deploy efetivamente faturada. Zero quando cortesia ou financiada integralmente. */
  readonly deployFaturado: Valor;
  /** Parcela do crédito consumida pelo deploy. O deploy tem prioridade sobre a mensalidade. */
  readonly creditoAplicadoNoDeploy: Valor;
  readonly creditoResidual: Valor;
  readonly reducaoMensal: Valor;

  /**
   * Mensalidade estimada: base mensal, mais as licenças de backup, mais a plataforma SafeStorage,
   * mais os custos adicionais, menos a redução do crédito.
   *
   * O crédito é subtraído do conjunto, e não apenas da infraestrutura: é isso que faz o Crédito
   * Especial AWS incidir também sobre o custo da plataforma SafeStorage e sobre os custos adicionais.
   */
  readonly mensalidadeEstimada: Valor;

  readonly economiaPercentual: number;
  /**
   * Se o bloco de economia deve ser renderizado. Reproduz o legado: só quando há crédito.
   * O percentual é calculado sempre. Ver decisão pendente P3.
   */
  readonly exibirEconomia: boolean;

  readonly avisos: readonly string[];
}

// ---------------------------------------------------------------------------
// Erros
// ---------------------------------------------------------------------------

export type CodigoErroPrecificacao =
  | 'PTAX_OBRIGATORIO'
  | 'PTAX_INVALIDO'
  | 'CONSUMO_AUSENTE'
  | 'CONSUMO_NEGATIVO'
  | 'CREDITO_NEGATIVO'
  | 'DEPLOY_NEGATIVO'
  | 'LICENCA_NEGATIVA'
  | 'MENSALIDADE_NEGATIVA'
  | 'PLANO_INDISPONIVEL_NA_TRILHA'
  | 'SAFESTORAGE_USUARIOS_INVALIDOS'
  | 'SAFESTORAGE_FORA_DA_FAIXA'
  | 'SAFESTORAGE_MENSALIDADE_AUSENTE'
  | 'CUSTO_ADICIONAL_NEGATIVO'
  | 'CUSTO_ADICIONAL_SEM_DESCRICAO';

export class ErroPrecificacao extends Error {
  readonly codigo: CodigoErroPrecificacao;
  readonly detalhe: Readonly<Record<string, unknown>>;

  constructor(
    codigo: CodigoErroPrecificacao,
    mensagem: string,
    detalhe: Readonly<Record<string, unknown>> = {},
  ) {
    super(mensagem);
    this.name = 'ErroPrecificacao';
    this.codigo = codigo;
    this.detalhe = detalhe;
  }
}
